import { Check, Sparkles, TriangleAlert } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MoneyValue } from './MoneyValue';
import { DocumentScanField } from './DocumentScanField';
import { useCartStore } from '@/stores/cart.store';
import { useGroupBookingTypes } from '@/hooks/queries';
import { useShallow } from 'zustand/react/shallow';
import type { PromoCandidate } from '@/types/coupon';
import type { DocumentType, PromoGroupType, SourceChannel } from '@/types/sale';
import { effectiveSourceChannel } from '@/lib/salesChannel';

/** Radix Select forbids an empty-string item value. */
const NONE = '__none__';

/**
 * The built-in group types, used until GET /group-booking-types answers (or if
 * it cannot be read). The list itself comes from the server, so a type an
 * admin adds on Group Booking Types (#1726) appears here too.
 */
const FALLBACK_GROUP_TYPES: { code: string; nameId: string }[] = [
  { code: 'FAMILY', nameId: 'Keluarga' },
  { code: 'SCHOOL', nameId: 'Sekolah' },
  { code: 'CORPORATE', nameId: 'Korporat' },
  { code: 'TOUR', nameId: 'Tur' },
  { code: 'EVENT', nameId: 'Acara' },
  { code: 'OTHER', nameId: 'Lainnya' },
];

const CHANNEL_LABELS: Record<string, string> = {
  web: 'Web',
  reseller: 'Reseller',
  kiosk: 'Kios',
  tiktok: 'TikTok',
  social: 'Media sosial',
  marketplace: 'Marketplace',
};
const CHANNELS = Object.keys(CHANNEL_LABELS) as SourceChannel[];

interface DiscountListProps {
  candidates: PromoCandidate[];
}

/** Does this promo gate on any cashier-set context? */
function needsContext(c: PromoCandidate): boolean {
  return Boolean(
    c.requires.groupTypes ||
      c.requires.sourceChannels ||
      c.requires.documentTypes?.length ||
      c.requires.documentType,
  );
}

/**
 * A localized notice for a promo that is ineligible because a USAGE CAP was
 * actually REACHED (the context — e.g. a scanned document — was already
 * provided). Without this the cashier would see the generic "complete the
 * requirements above" even though the requirements are met, and wonder why the
 * discount didn't apply.
 *
 * Only fires on a genuinely-reached cap: the backend uses the word "reached".
 * The similar-but-different reasons "Document could not be identified for the
 * per-customer limit" (no document scanned yet) and "Per-customer usage limit
 * could not be verified" (DB error) also mention "per-customer" but must NOT
 * show "limit reached" — otherwise the warning appears BEFORE the cashier has
 * uploaded any document. Those fall through to the ordinary "scan below" hint.
 */
function limitNotice(c: PromoCandidate): string | null {
  if (c.eligible) return null;
  const reason = c.reason ?? '';
  if (!/reached/i.test(reason) || !/usage limit|per-customer/i.test(reason)) return null;
  return c.requires.documentTypes?.length || c.requires.documentType
    ? 'Dokumen ini sudah mencapai batas pemakaian promo per pelanggan.'
    : 'Promo ini sudah mencapai batas pemakaiannya.';
}

export function DiscountList({ candidates }: DiscountListProps) {
  const { selected, toggle, promo, setPromo } = useCartStore(
    useShallow((s) => ({
      selected: s.discountIds,
      toggle: s.toggleDiscount,
      promo: s.promo,
      setPromo: s.setPromo,
    })),
  );
  // Every group type, built-in and admin-added (#1726); the built-in six until it loads.
  const { data: loadedGroupTypes } = useGroupBookingTypes();
  const groupTypes = loadedGroupTypes?.length ? loadedGroupTypes : FALLBACK_GROUP_TYPES;

  if (candidates.length === 0) return null;

  /**
   * Picking a promo auto-fills the single-value group/channel context it needs,
   * so it qualifies on the spot. Document gates can't auto-fill — the cashier
   * scans below. Turning a promo off leaves the context as-is (harmless when no
   * gated promo is selected).
   */
  const onToggle = (c: PromoCandidate) => {
    const willSelect = !selected.includes(c.id);
    if (willSelect) {
      const { groupTypes, sourceChannels } = c.requires;
      if (groupTypes?.length && !groupTypes.includes(promo.groupType ?? '')) {
        setPromo({ groupType: groupTypes[0] as PromoGroupType });
      }
      // No pick means the ticketbox's own channel — that is what the sale sends.
      if (sourceChannels?.length && !sourceChannels.includes(effectiveSourceChannel(promo.sourceChannel))) {
        setPromo({ sourceChannel: sourceChannels[0] as SourceChannel });
      }
    }
    toggle(c.id);
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-gold-600" /> Promo tersedia
      </Label>
      <div className="space-y-2">
        {candidates.map((c) => {
          const isOn = selected.includes(c.id);
          const showReveal = isOn && needsContext(c);
          return (
            <div key={c.id} className="rounded-md border-2 border-border bg-card">
              <label className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:border-primary/30">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink-900">{c.name}</p>
                  {c.potentialSavings > 0 && (
                    <p className="text-xs text-success">
                      Hemat{' '}
                      <MoneyValue value={c.potentialSavings} className="text-xs text-success" />
                    </p>
                  )}
                </div>
                <Switch checked={isOn} onCheckedChange={() => onToggle(c)} />
              </label>

              {showReveal && (
                <div className="space-y-3 border-t border-border px-4 py-3">
                  {c.requires.groupTypes && (
                    <div className="space-y-1.5">
                      <Label htmlFor={`grp-${c.id}`}>Segmen grup</Label>
                      <Select
                        value={promo.groupType ?? NONE}
                        onValueChange={(v) =>
                          setPromo({ groupType: v === NONE ? null : (v as PromoGroupType) })
                        }
                      >
                        <SelectTrigger id={`grp-${c.id}`} aria-label="Segmen grup">
                          <SelectValue placeholder="Pilih segmen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>Tanpa segmen</SelectItem>
                          {groupTypes.map((g) => (
                            <SelectItem key={g.code} value={g.code}>
                              {g.nameId}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {c.requires.sourceChannels && (
                    <div className="space-y-1.5">
                      <Label htmlFor={`chan-${c.id}`}>Kanal penjualan</Label>
                      <Select
                        value={promo.sourceChannel ?? NONE}
                        onValueChange={(v) =>
                          setPromo({ sourceChannel: v === NONE ? null : (v as SourceChannel) })
                        }
                      >
                        <SelectTrigger id={`chan-${c.id}`} aria-label="Kanal penjualan">
                          <SelectValue placeholder="Pilih kanal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>Ticketbox (bawaan)</SelectItem>
                          {CHANNELS.map((ch) => (
                            <SelectItem key={ch} value={ch}>
                              {CHANNEL_LABELS[ch]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {(c.requires.documentTypes?.length || c.requires.documentType) && (
                    <DocumentScanField
                      allowedTypes={
                        (c.requires.documentTypes?.length
                          ? c.requires.documentTypes
                          : [c.requires.documentType]) as DocumentType[]
                      }
                    />
                  )}

                  {c.eligible ? (
                    <p className="flex items-center gap-1.5 text-xs font-medium text-success">
                      <Check className="h-3.5 w-3.5" /> Syarat terpenuhi — promo diterapkan.
                    </p>
                  ) : limitNotice(c) ? (
                    <p className="flex items-center gap-1.5 text-xs font-medium text-warning">
                      <TriangleAlert className="h-3.5 w-3.5 shrink-0" /> {limitNotice(c)}
                    </p>
                  ) : (
                    <p className="text-xs text-ink-400">
                      Lengkapi syarat di atas agar promo bisa diterapkan.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
